import React from 'react';
import { Box, Typography } from '@mui/material';
import { Button } from 'reactstrap';
import colors from 'src/styles/equalution/colors';
import CoachingIcon from 'src/assets/icons/coaching.svg';
import { Product } from 'src/pages/deprecated/payment/types';
import { getNutritionCoachProductPrice } from 'src/pages/signup/steps/extras/utils/challengeAddon.util';
import { InfoBox } from 'src/components/info-box/InfoBox';
import moment from 'moment';
import WarningGreenIcon from 'src/assets/icons/warningGreen.svg';

interface Props {
  products: Product[] | undefined;
  isSubscribed: boolean;
  onAdd: () => void;
  onRemove: () => void;
  isLoading?: boolean;
  endDate?: string | null;
  nextBillingDate?: string | null;
}

export const CoachingCardAddons: React.FC<Props> = ({
  products,
  isSubscribed,
  onAdd,
  onRemove,
  isLoading = false,
  endDate,
  nextBillingDate,
}) => {
  const priceInfo = getNutritionCoachProductPrice(products || []);
  const price = priceInfo?.price || 0;

  const formattedEndDate = endDate
    ? moment(endDate).format('MMMM Do, YYYY')
    : '';

  const nextBillingDateFormatted = nextBillingDate
    ? moment(nextBillingDate).format('DD/MM/YYYY')
    : '';

  return (
    <Box
      sx={{
        backgroundColor: colors.OFF_WHITE,
        borderRadius: '20px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}>
      {/* Icon and Title Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          marginBottom: '4px',
        }}>
        <Box
          sx={{
            minWidth: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
          <img src={CoachingIcon} alt="Coaching Icon" width={40} height={40} />
        </Box>

        <Typography
          sx={{
            fontSize: '16px',
            fontWeight: 700,
            color: colors.OFF_BLACK,
            lineHeight: '24px',
            textTransform: 'uppercase',
          }}>
          COACHING ADD-ON
        </Typography>
      </Box>

      {/* Divider */}
      <Box
        sx={{
          width: '25%',
          height: '1px',
          backgroundColor: colors.BLUEBERRY_HALF,
          opacity: 0.5,
        }}
      />

      {/* Pricing/Description and Button Row */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
        }}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            flex: 1,
          }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0 }}>
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 700,
                color: colors.SALAD,
                lineHeight: '32px',
              }}>
              ${price.toFixed(2)}
            </Typography>
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 400,
                color: colors.SALAD,
                lineHeight: '24px',
              }}>
              /month
            </Typography>
          </Box>

          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: colors.OFF_BLACK,
              paddingRight: '60px',
            }}>
            1:1 nutrition coaching to hit your goals faster. Cancel anytime.
          </Typography>
        </Box>

        {isSubscribed ? (
          <Button
            disabled={isLoading}
            onClick={onRemove}
            outline
            style={{ width: '100px' }}
            size="sm">
            Remove
          </Button>
        ) : (
          <Button
            disabled={isLoading || !priceInfo}
            onClick={onAdd}
            style={{ width: '72px' }}
            size="sm">
            Add
          </Button>
        )}
      </Box>

      {Boolean(nextBillingDateFormatted) && (
        <Typography
          fontSize={'12px'}
          fontWeight={400}
          color={colors.OFF_BLACK}
          mt={1}>
          Next billing date is on {nextBillingDateFormatted}
        </Typography>
      )}

      {/* Info Box for the coaching add-on */}
      {Boolean(formattedEndDate) && (
        <InfoBox
          icon={WarningGreenIcon}
          title={`Your Coaching Add-On will end on ${formattedEndDate}`}
          containerStyle={{ backgroundColor: colors.WHITE, marginTop: '10px' }}
        />
      )}
    </Box>
  );
};
